import { change, ChangeState, createChangeState } from "@greeter/changes";
import { ImageAsset, VenueProduct, emptyVenueProduct } from "@greeter/core";
import { adapt, notImplemented } from "@greeter/xstate-util";
import { assign, emit, raise, setup } from "xstate";
import { IVenueApi } from "@greeter/venue-api";
import { ApiVersions } from "@greeter/api";

const requestOpts = { apiVersion: ApiVersions.V2 };

export interface ProductEditorApiRequirements
  extends Pick<IVenueApi, "fetchProduct" | "uploadAsset" | "saveProduct"> {}

export function provideApiForActor(
  m: typeof productEditorMachine,
  api: ProductEditorApiRequirements
) {
  return m.provide({
    actors: {
      uploadAsset: adapt(api.uploadAsset.bind(api)),
      fetchProduct: adapt(api.fetchProduct.bind(api)),
      saveProduct: adapt(api.saveProduct.bind(api)),
    },
  });
}

export type ProductChange =
  | { type: "setTitle"; title: string }
  | { type: "setDescription"; description: string }
  | { type: "setPrice"; price: number }
  | { type: "setCost"; cost: number }
  | { type: "setCover"; cover: ImageAsset }
  | { type: "setCategoryId"; data: string };

export type ProductChanges = Array<ProductChange>;

function apply(p: VenueProduct, change: ProductChange) {
  switch (change.type) {
    case "setTitle":
      p.title = change.title;
      break;

    case "setDescription":
      p.description = change.description;
      break;

    case "setPrice":
      p.price = change.price;
      break;

    case "setCost":
      p.cost = change.cost;
      break;

    case "setCover":
      p.coverAssetId = change.cover.id;
      p.coverAsset = change.cover;
      break;

    // case "setActive":
    //   p.active = change.active;
    //   break;

    case "setCategoryId":
      p.categories = [change.data];
      break;
  }
}

type ProductEditorContext = {
  state: ChangeState<VenueProduct, ProductChange>;
  productId?: string;
  fileToUpload?: File;
};

type ChangeEvent = {
  type: "change";
  data: ProductChange;
};

type ProductEditorEvents =
  | ChangeEvent
  | { type: "fetchProduct" }
  | { type: "setUploadAsset"; data: File }
  | { type: "uploadAsset" }
  | { type: "save" };

type ProductEditorEmitted =
  | { type: "saving" }
  | { type: "saved" }
  | { type: "saveProduct.failed"; error: Error }
  | { type: "uploadedAsset" }
  | { type: "uploadingAsset" }
  | { type: "uploadAsset.failed"; error: Error }
  | { type: "fetchProduct.failed"; error: Error };

export const productEditorMachine = setup({
  types: {} as {
    context: ProductEditorContext;
    events: ProductEditorEvents;
    emitted: ProductEditorEmitted;
    input: {
      productId: string;
    };
  },
  actors: {
    fetchProduct:
      notImplemented<ProductEditorApiRequirements["fetchProduct"]>(),
    uploadAsset: notImplemented<ProductEditorApiRequirements["uploadAsset"]>(),
    saveProduct: notImplemented<ProductEditorApiRequirements["saveProduct"]>(),
  },
}).createMachine({
  type: "parallel",
  context: ({ input }) => {
    return {
      state: createChangeState(emptyVenueProduct()),
      productId: input?.productId,
    };
  },
  states: {
    api: {
      type: "parallel",
      states: {
        assets: {
          initial: "idle",
          states: {
            idle: {
              on: {
                setUploadAsset: {
                  actions: assign(({ event }) => ({
                    fileToUpload: event.data,
                  })),
                },
                uploadAsset: {
                  target: "uploading",
                  guard: ({ context }) => {
                    return !!context.fileToUpload;
                  },
                },
              },
            },
            uploading: {
              entry: emit({ type: "uploadingAsset" }),
              invoke: {
                src: "uploadAsset",
                input: ({ context }) => [{ data: context.fileToUpload! }],
                onDone: {
                  actions: [
                    raise(({ event }) => ({
                      type: "change",
                      data: { type: "setCover", cover: event.output },
                    })),
                    emit({ type: "uploadedAsset" }),
                  ],
                },
                onError: {
                  target: "idle", // Might simply be "does not exist"
                  actions: [
                    emit(({ event }) => ({
                      type: "fetchProduct.failed",
                      error: event.error as Error,
                    })),
                  ],
                },
              },
            },
          },
        },
        product: {
          initial: "idle",
          entry: raise({ type: "fetchProduct" }),
          states: {
            idle: {
              on: {
                fetchProduct: {
                  target: "fetching",
                  guard({ context }) {
                    return !!context.productId;
                  },
                },
                save: {
                  target: "saving",
                  guard({ context }) {
                    return !!context.state.value;
                  },
                },
              },
            },
            fetching: {
              invoke: {
                src: "fetchProduct",
                input({ context }) {
                  return [context.productId!, requestOpts];
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return {
                      state: createChangeState(event.output),
                    };
                  }),
                },
                onError: {
                  target: "idle", // Might simply be "does not exist"
                  actions: [
                    emit(({ event }) => ({
                      type: "fetchProduct.failed",
                      error: event.error as Error,
                    })),
                  ],
                },
              },
            },
            saving: {
              entry: emit({ type: "saving" }),
              invoke: {
                src: "saveProduct",
                input({ context }) {
                  return [context.state.value!, requestOpts];
                },
                onDone: {
                  target: "idle",
                  actions: emit({ type: "saved" }),
                },
                onError: {
                  target: "idle", // Might simply be "does not exist"
                  actions: [
                    emit(({ event }) => ({
                      type: "saveProduct.failed",
                      error: event.error as Error,
                    })),
                  ],
                },
              },
            },
          },
        },
      },
    },
    ui: {
      initial: "editing",
      states: {
        editing: {
          on: {
            change: {
              actions: [
                assign(({ context, event }) => {
                  return {
                    state: { ...change(context.state, apply, event.data) },
                  };
                }),
              ],
            },
          },
        },
      },
    },
  },
});
